import {
  forwardRef,
  Input,
  InputGroup,
  InputLeftAddon,
  InputProps,
  useMergeRefs,
} from '@chakra-ui/react';
import { FieldError } from 'features';
import { useEffect, useRef } from 'react';
import { KeyboardService } from 'services';

export interface AppInputProps extends InputProps {
  leftAddon?: string;
  showAddon?: boolean;
  mode?: 'regular' | 'number' | 'currency';
  focusOnMount?: boolean;
  mountErrorField?: boolean;
}

export const AppInput = forwardRef((props: AppInputProps, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRefs(ref, inputRef);

  const {
    leftAddon: _,
    showAddon = true,
    onKeyDown,
    mode = 'regular',
    focusOnMount,
    mountErrorField = true,
    ...rest
  } = props;

  const changeValue = () => {
    const original = props.value as string;
    const altered = KeyboardService.addFloatingDecimal(original);
    if (original !== altered) {
      props.onChange?.({ target: { value: altered } } as any);
    }
  };
  let { leftAddon } = props;

  if (mode === 'currency' && showAddon) {
    leftAddon = '$';
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    props.onKeyDown?.(e as any);

    if (mode === 'regular') {
      return;
    }

    if (KeyboardService.isCommitKey(e)) {
      changeValue();
      return;
    }

    if (!KeyboardService.isNumeralKey(e)) {
      e.preventDefault();
    }
  };

  const handleBlur = (e: any) => {
    props.onBlur?.(e as any);

    if (mode !== 'currency') {
      return;
    }

    changeValue();
  };

  const handleFocus = (e: any) => {
    props.onFocus?.(e);
    const elem = inputRef.current;

    if (elem) {
      const original = elem.type;
      elem.type = 'text';
      elem.setSelectionRange(0, elem.value.length);
      elem.type = original;
    }
  };

  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [focusOnMount]);

  return (
    <>
      <InputGroup>
        {leftAddon && <InputLeftAddon>{leftAddon}</InputLeftAddon>}
        <Input
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          ref={mergedRef}
          {...rest}
        />
      </InputGroup>
      {mountErrorField && <FieldError name={props.name} />}
    </>
  );
});
